import { css } from "@compiled/react";
import { defineMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";
import { MotionContainer } from "@/patterns/global-nav/src/components/motion-container";
import { RowGrid } from "@/patterns/global-nav/src/layouts/row-grid";
import { LinkColumn } from "@/patterns/global-nav/src/layouts/subnav-link-columns";
import { DivOnHoverExit } from "@/patterns/global-nav/src/nav-links-container";

function useLoomSolutionsLinks() {
    return {
        features: {
            id: "teams",
            title: defineMessage({
                id: "loom-product-nav.solutions.links.solution",
                defaultMessage: "Teams"
            }),
            items: [
                {
                    id: "sales",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.sales",
                        defaultMessage: "Sales"
                    }),
                    href: useLocalizedHref("/software/loom/use-case/sales")
                },
                {
                    id: "marketing",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.marketing",
                        defaultMessage: "Marketing"
                    }),
                    href: useLocalizedHref("/software/loom/use-case/marketing")
                },
                {
                    id: "design",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.design",
                        defaultMessage: "Design"
                    }),
                    href: useLocalizedHref("/software/loom/use-case/design")
                },
                {
                    id: "support",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.support",
                        defaultMessage: "Support"
                    }),
                    href: useLocalizedHref("/software/loom/use-case/support")
                },
                {
                    id: "productmanagement",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.product-management",
                        defaultMessage: "Product Management"
                    }),
                    href: useLocalizedHref(
                        "/software/loom/use-case/product-management"
                    )
                },
                {
                    id: "engineering",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.engineering",
                        defaultMessage: "Engineering"
                    }),
                    href: useLocalizedHref(
                        "/software/loom/use-case/engineering"
                    )
                }
            ]
        },
        usecases: {
            id: "usecases",
            title: defineMessage({
                id: "loom-product-nav.solutions.links.use-cases",
                defaultMessage: "Use cases"
            }),
            items: [
                {
                    id: "loom-jira",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.loom-jira",
                        defaultMessage: "Loom + Jira"
                    }),
                    href: useLocalizedHref("/software/loom/loom-jira")
                },
                {
                    id: "loom-confluence",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.loom-confluence",
                        defaultMessage: "Loom + Confluence"
                    }),
                    href: useLocalizedHref("/software/loom/loom-confluence")
                }
            ]
        },
        companysize: {
            id: "companysize",
            title: defineMessage({
                id: "loom-product-nav.solutions.links.company-size",
                defaultMessage: "Company Size"
            }),
            items: [
                {
                    id: "enterprise",
                    text: defineMessage({
                        id: "loom-product-nav.solutions.links.enterprise",
                        defaultMessage: "Enterprise"
                    }),
                    href: useLocalizedHref("/software/loom/enterprise")
                }
            ]
        }
    } as const;
}

export const LoomSolutionsSubnav = ({
    productKey = "loom"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    const loomSolutionLinks = useLoomSolutionsLinks();
    const linkColumns = Object.values(loomSolutionLinks);

    return (
        <DivOnHoverExit>
            <TypographyStyles>
                <MotionContainer direction="up">
                    <div
                        css={css({
                            backgroundColor: wacToken("color.neutral.light0"),
                            [media.below.md]: {
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                [`> div`]: {
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "40px"
                                }
                            }
                        })}
                    >
                        <RowGrid numCols={3} columnGap={token("space.500")}>
                            {linkColumns.map((column) => {
                                return (
                                    <LinkColumn key={column.id}>
                                        <span
                                            css={css({
                                                textTransform: "uppercase",
                                                color: wacToken(
                                                    "color.primary.black"
                                                ),
                                                fontSize: "0.75rem",
                                                fontFamily:
                                                    wacToken(
                                                        "font.charlie.text"
                                                    ),
                                                fontWeight: 700,
                                                lineHeight: "1rem"
                                            })}
                                        >
                                            {intl.formatMessage(column.title)}
                                        </span>
                                        <div
                                            css={css({
                                                display: "grid",
                                                gridAutoRows: "40px"
                                            })}
                                        >
                                            {column.items.map((link) => {
                                                return (
                                                    <div
                                                        key={link.id}
                                                        css={css({
                                                            // nav doesn't follow the typography
                                                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                                            a: {
                                                                "fontWeight": 400,
                                                                "fontSize":
                                                                    "0.875rem",
                                                                "color":
                                                                    wacToken(
                                                                        "color.primary.black"
                                                                    ),
                                                                "&:visited": {
                                                                    textDecoration:
                                                                        "none",
                                                                    color: wacToken(
                                                                        "color.primary.black"
                                                                    )
                                                                },
                                                                "&:hover, &:focus":
                                                                    {
                                                                        color: wacToken(
                                                                            "color.extended.blue800"
                                                                        )
                                                                    }
                                                            },
                                                            display: "flex",
                                                            alignItems: "end"
                                                        })}
                                                    >
                                                        {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                                                        <Link
                                                            type="anchor"
                                                            href={link.href}
                                                            text={intl.formatMessage(
                                                                link.text
                                                            )}
                                                            ariaLabel={intl.formatMessage(
                                                                link.text
                                                            )}
                                                            dataEventProps={{
                                                                "data-event":
                                                                    "clicked",
                                                                "data-event-component":
                                                                    "linkButton",
                                                                "data-event-container":
                                                                    "globalNavSubnav",
                                                                "data-product-key":
                                                                    productKey
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </LinkColumn>
                                );
                            })}
                        </RowGrid>
                    </div>
                </MotionContainer>
            </TypographyStyles>
        </DivOnHoverExit>
    );
};
